import type { ReactNode } from 'react'
import classNames from 'classnames'
import { AlertTriangle } from 'lucide-react'
import Button from '@/components/button'
import EmptyState from '@/components/empty-state'
import Pagination from '@/components/pagination'
import Skeleton from '@/components/skeleton'
import type { PagedResult } from '@/types/api'
import { toErrorMessage } from '@/services/errors'
import styles from './style.module.css'

export interface Column<T> {
  key: string
  header: ReactNode
  render: (row: T) => ReactNode
  width?: string
  align?: 'center' | 'left' | 'right'
}

interface DataTableProps<T> {
  columns: Column<T>[]
  page: PagedResult<T> | undefined
  getRowKey: (row: T) => string
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
  onRowClick?: (row: T) => void
  onPageChange: (pageNumber: number) => void
  pageSize: number
  onPageSizeChange?: (pageSize: number) => void
  emptyTitle?: string
  emptyDescription?: ReactNode
  toolbar?: ReactNode
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]
const SKELETON_ROWS = 8

const DataTable = <T,>({
  columns,
  emptyDescription,
  emptyTitle,
  error,
  getRowKey,
  isLoading = false,
  onPageChange,
  onPageSizeChange,
  onRetry,
  onRowClick,
  page,
  pageSize,
  toolbar,
}: DataTableProps<T>) => {
  const rows = page?.items ?? []
  const hasError = Boolean(error) && !isLoading
  const showEmptyState = !isLoading && !hasError && rows.length === 0

  return (
    <section className={styles.wrapper}>
      {toolbar ? <div className={styles.toolbar}>{toolbar}</div> : null}

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{ width: column.width, textAlign: column.align ?? 'left' }}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {isLoading
              ? Array.from({ length: SKELETON_ROWS }).map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {columns.map((column) => (
                      <td key={column.key}>
                        <Skeleton variant="text" height={14} />
                      </td>
                    ))}
                  </tr>
                ))
              : rows.map((row) => (
                  <tr
                    key={getRowKey(row)}
                    className={classNames({ [styles.clickable]: Boolean(onRowClick) })}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((column) => (
                      <td key={column.key} style={{ textAlign: column.align ?? 'left' }}>
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {hasError ? (
        <EmptyState
          icon={<AlertTriangle size={28} />}
          title="Não foi possível carregar os dados"
          description={toErrorMessage(error)}
          action={
            onRetry ? (
              <Button variant="outline" onClick={onRetry}>
                Tentar novamente
              </Button>
            ) : undefined
          }
        />
      ) : showEmptyState ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <div className={styles.footer}>
          {onPageSizeChange ? (
            <label className={styles.pageSize}>
              Ver:
              <select
                value={pageSize}
                onChange={(event) => onPageSizeChange(Number(event.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <Pagination
            pageIndex={page?.pageIndex ?? 1}
            totalPages={page?.totalPages ?? 0}
            totalItems={page?.totalItems ?? 0}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </section>
  )
}

export default DataTable
